import { useCallback, useEffect, useState } from "react";
import { loadCreditBalance, loadCreditPrices } from "../apiClient.js";
import { isAuthEnabled } from "./supabaseClient.js";

export interface CreditsState {
  /** Balance in credits (1 credit = 1 cent of list price), or null when auth is off / signed out / not yet loaded. */
  balanceCents: number | null;
  /**
   * True until the signed-in user's first balance read settles, so the chrome
   * can hold the balance's place instead of popping it in. A failed read ends it
   * too (the balance then stays hidden); later refreshes never set it again.
   */
  balanceLoading: boolean;
  /** Operation → price in credits, or null until loaded. */
  prices: Record<string, number> | null;
  /** Re-fetch the balance from the server (call after a spend or a purchase). */
  refresh: () => void;
}

/**
 * App-owned credit state for the signed-in user. The balance is re-fetched when
 * `userId` changes and on demand via `refresh` (after any credit-spending
 * action); the price table is static per deployment and loaded once. When auth
 * is disabled (file mode / tests) both stay null so the UI hides credit chrome.
 */
export function useCredits(userId: string | null | undefined): CreditsState {
  const [balanceCents, setBalanceCents] = useState<number | null>(null);
  // The user whose first balance read has settled. Derived rather than toggled
  // in the effect, so the very render that learns the user already reads as
  // loading (no frame without the placeholder).
  const [balanceSettledFor, setBalanceSettledFor] = useState<string | null>(null);
  const [prices, setPrices] = useState<Record<string, number> | null>(null);

  const refresh = useCallback(() => {
    if (!isAuthEnabled || !userId) {
      setBalanceCents(null);
      return;
    }
    void loadCreditBalance()
      .then(setBalanceCents)
      .catch(() => {
        // A transient balance-read failure must not blank a known balance.
      })
      .finally(() => setBalanceSettledFor(userId));
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!isAuthEnabled || !userId) {
      setPrices(null);
      return;
    }
    if (prices) return;
    void loadCreditPrices()
      .then((result) => setPrices(result.prices))
      .catch(() => {
        // Prices are advisory chrome; a failure just hides the cost hints.
      });
  }, [userId, prices]);

  const balanceLoading = isAuthEnabled && Boolean(userId) && balanceSettledFor !== userId;
  return { balanceCents, balanceLoading, prices, refresh };
}
